import React, { useContext, useEffect, useState } from "react";
import { db, firebaseRemoteConfig } from "../firebase";
import { getAll } from "firebase/remote-config";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where
} from "firebase/firestore";
import { useAuth } from "./AuthProvider";
import {
  getDaysAgo,
  standardDeviation,
  convertTomm,
  roundOffTwo,
  roundOff,
  isNotExists
} from "../utils/common_handlers";
var moment = require("moment");

const UsersDataContext = React.createContext();

export const useUsersData = () => {
  return useContext(UsersDataContext);
};

let snapShotListeners = [];
export const UsersDataProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [Users, setUsers] = useState([]);
  const [studentIds, setStudentIds] = useState([]);
  const [remoteConfigData, setRemoteConfigData] = useState(null);
  const UsersRef = React.useRef(Users);
  const { currentUser, userRole } = useAuth();

  useEffect(() => {
    UsersRef.current = Users;
  }, [Users]);

  useEffect(() => {
    if (currentUser) {
      getRemoteConfigs();
    }
  }, [currentUser]);

  useEffect(() => {
    async function getUsers() {
      setUsers([]);
      //setLoading(true);
      if (currentUser) {
        if (userRole && userRole === "admin") {
          completUsersSnapShot();
        } else if (userRole && userRole === "sub-admin") {
          await getUsersAssignedToSubAdmin();
        } else if (userRole && userRole === "teacher") {
          await getUsersAssignedToSubAdmin();
        } else if (userRole && userRole === "optician") {
          await getUsersAssignedToSubAdmin();
        }
      }
    }
    getUsers();

    return () => {
      snapShotListeners.forEach(unsub => {
        unsub();
      });
    };
  }, [currentUser, remoteConfigData]);

  const getUsersAssignedToSubAdmin = async () => {
    const docRef = doc(db, "SubAdminData", currentUser.uid);
    const adminSnapData = await getDoc(docRef);
    if (adminSnapData.exists()) {
      setStudentIds(
        adminSnapData.data().AssignedUsers
          ? adminSnapData.data().AssignedUsers
          : []
      );
    }
  };

  useEffect(() => {
    setLoading(true);
    const collRef = collection(db, "UserData");
    setUsers([]);
    if (studentIds.length > 0) {
      let ids = [];
      for (let i = 0; i < studentIds.length; i++) {
        if (ids.length <= 10) {
          ids.push(studentIds[i]);
        }
        if (ids.length === 10 || i === studentIds.length - 1) {
          const queryUsers = query(collRef, where(documentId(), "in", ids));
          const snapSubscription = onSnapshot(queryUsers, data => {
            if (data.size) {
              setLoading(false);
            }
            processData(data);
          });
          snapShotListeners.push(snapSubscription);
          ids = [];
        }
      }
    } else {
      setLoading(false);
    }
    return () => {
      snapShotListeners.forEach(unsub => {
        unsub();
      });
    };
  }, [studentIds]);

  const completUsersSnapShot = () => {
    const snapSubscription = onSnapshot(
      collection(db, "UserData"),
      data => {
        if (data.size) {
          setLoading(false);
        } else {
          setLoading(false);
        }
        processData(data);
      },
      error => {
        setError(error.message);
        console.log(error.message);
      }
    );
    snapShotListeners.push(snapSubscription);
  };

  const processData = async data => {
    let newUsers = [...UsersRef.current]; // Copy the current users

    const changes = data.docChanges(); // Get changes from the Firestore snapshot
    const promises = changes.map(async change => {
      // Map over each document change
      try {
        let CalibScore = null; // Initialize calibration score
        let sign = "flat"; // Initialize sign

        // Get the number of days since last session
        let daysAgo =
          change.doc.data().LastSessionDoneAt &&
          getDaysAgo(
            change.doc
              .data()
              .LastSessionDoneAt.toDate()
              .toDateString()
          );
        let obj = {
          uid: change.doc.id, // Get the user id
          ...change.doc.data(), // Copy all fields from the document
          SessionDoneDaysAgo: daysAgo // Add the number of days since last session
        };
        if (
          !(
            change.doc.data().ValidTill &&
            moment(
              change.doc
                .data()
                .ValidTill.toDate()
                .toDateString()
            ).isBefore()
          )
        ) {
          // Initialize calibration ID arrays
          let prevCalibrationIds =
            change.doc.data().PrevCalibrationResult || [];
          let calibrationIds = [];

          // If a calibration result exists, add it to the calibration IDs
          if (change.doc.data().CalibrateResult) {
            calibrationIds.push(change.doc.data().CalibrateResult.id);
          }

          // Add all previous calibration IDs
          if (prevCalibrationIds.length > 0) {
            for (let index = 0; index < prevCalibrationIds.length; index++) {
              const element = prevCalibrationIds[index];
              calibrationIds.push(element.id);
            }
          }
          let nextTestDate = "";
          let testDate = moment(change.doc.data().ValidTill.toDate());

          // Check if the next test date is in the future
          if (change.doc.data().ValidTill) {
            let currentDate = moment();
            if (testDate.isSameOrAfter(currentDate)) {
              nextTestDate = testDate;
            }
            // Keep subtracting 6 weeks from the next test date until it is in the past
            while (
              testDate.isSameOrAfter(currentDate) &&
              testDate.isSameOrAfter(currentDate.add(6, "weeks"))
            ) {
              testDate.subtract(6, "weeks");
              nextTestDate = testDate;
              currentDate = moment();
            }
          }

          // Add the next test date to the user object
          obj = {
            ...obj,
            nextTestDate: nextTestDate
          };

          // Initialize calibration data array
          let calibrationData = [];
          const calibrationDocRef = collection(db, "CalibrateResult");

          // Fetch all calibration data in chunks of 10
          for (let i = 0; i < calibrationIds.length; i += 10) {
            const calibrationIdsChunk = calibrationIds.slice(i, i + 10);
            const docSnap = query(
              calibrationDocRef,
              where(documentId(), "in", calibrationIdsChunk)
            );
            const calibrationDocsSnap = await getDocs(docSnap);

            // Process each chunk of calibration data
            let calibrationDataChunk = await Promise.all(
              calibrationDocsSnap.docs.map(async doc => {
                // Process each document in the chunk
                let avg = !isNotExists(doc.data().RecordedDevice)
                  ? doc.data().CalibrationAvg
                  : convertTomm(doc.data().CalibrationAvg);
                let calibration5Data = doc.data().CalibrationData;
                let RecordedDeviceData = doc.data().RecordedDevice
                  ? await getDoc(doc.data().RecordedDevice).then(data =>
                      data.exists() ? data.data() : null
                    )
                  : null;
                if (!isNotExists(doc.data().RecordedDevice)) {
                  calibration5Data = calibration5Data.map(item =>
                    roundOffTwo(item)
                  );
                }
                return {
                  uid: doc.id,
                  ...doc.data(),
                  CalibrationAvg: avg,
                  CalibrationData: calibration5Data,
                  RecordedDeviceData
                };
              })
            );

            // Combine the chunks of calibration data
            calibrationData = [...calibrationData, ...calibrationDataChunk];
          }

          // Sort calibrationData
          calibrationData.sort(function(a, b) {
            return (
              new Date(b.CalibratedOnDate.toDate()) -
              new Date(a.CalibratedOnDate.toDate())
            );
          });

          // Get the calibration averages from the last five calibrations
          let lastFiveCalibrationAvgs = calibrationData
            .slice(0, 5)
            .map(data => data.CalibrationAvg);

          let standardDeviationValue = "";
          // let standardDeviationPercentage = "";
          let CalibrationStandardDeviation = "";
          // let CalibrationStandardDeviationPercentage = "";

          if (lastFiveCalibrationAvgs.length === 5) {
            let { deviationValue } = standardDeviation(lastFiveCalibrationAvgs);
            standardDeviationValue = roundOffTwo(deviationValue);
            // standardDeviationPercentage = roundOffTwo(deviationPercentage);
          }
          if (calibrationData.length > 0) {
            let { deviationValue } = standardDeviation(
              calibrationData[0].CalibrationData
            );
            CalibrationStandardDeviation = roundOffTwo(deviationValue);
            // CalibrationStandardDeviationPercentage = roundOffTwo(
            //   deviationPercentage
            // );
          }

          // dividing two calibs by calib score
          if (standardDeviationValue && CalibScore) {
            standardDeviationValue =
              Number(standardDeviationValue) / Number(CalibScore);
          }
          if (CalibrationStandardDeviation && CalibScore) {
            CalibrationStandardDeviation =
              Number(CalibrationStandardDeviation) / Number(CalibScore);
          }

          obj = {
            ...obj,
            StandardDeviation: standardDeviationValue,
            FirstSessionDoneOn:
              calibrationData.length > 0
                ? calibrationData[calibrationData.length - 1].CalibratedOnDate
                : "",
            StandardDeviationIcon:
              standardDeviationValue &&
              remoteConfigData &&
              remoteConfigData.Training_Standard_Deviation
                ? standardDeviationValue >
                  remoteConfigData.Training_Standard_Deviation._value
                  ? true
                  : false
                : false,
            CalibrationStandardDeviation: CalibrationStandardDeviation,
            CalibrationStandardDeviationIcon:
              CalibrationStandardDeviation &&
              remoteConfigData &&
              remoteConfigData.Calibration_Standard_Deviation
                ? CalibrationStandardDeviation >
                  remoteConfigData.Calibration_Standard_Deviation._value
                  ? true
                  : false
                : false
          };
          // Standard deviation calculation end
        }
        // Any changes made by user start
        obj = {
          ...obj,
          ChangesMade: detectChanges(obj)
        };
        // Any changes made by user end
        if (change.type === "added") {
          if (change.doc.data().CalibrateResult) {
            let last_three = change.doc.data().PrevCalibrationResult.slice(-3);
            const docSnap = await getDoc(change.doc.data().CalibrateResult);
            if (docSnap.exists()) {
              CalibScore = roundOff(docSnap.data().CalibrationAvg);
            }
            if (CalibScore && last_three.length > 0) {
              const docRef = collection(db, "CalibrateResult");
              const docSnaps = query(
                docRef,
                where(documentId(), "in", last_three)
              );
              const ddd = await getDocs(docSnaps);
              let last_three_score = 0;
              ddd.forEach((a, index) => {
                last_three_score = last_three_score + a.data().CalibrationAvg;
              });
              last_three_score = last_three_score / 3;
              if (last_three_score > CalibScore) {
                sign = "down";
              } else if (last_three_score < CalibScore) {
                sign = "up";
              }
            }
          }
          newUsers.push({ ...obj, CalibScore, sign });
        }
        if (change.type === "modified") {
          let newArray = [...UsersRef.current];
          let index = newArray.findIndex(user => change.doc.id === user.uid);
          if (change.doc.data().CalibrateResult) {
            let last_three = change.doc.data().PrevCalibrationResult.slice(-3);
            const docSnap = await getDoc(change.doc.data().CalibrateResult);
            if (docSnap.exists()) {
              CalibScore = roundOff(docSnap.data().CalibrationAvg);
            }
            if (CalibScore && last_three.length > 0) {
              const docRef = collection(db, "CalibrateResult");
              const docSnaps = query(
                docRef,
                where(documentId(), "in", last_three)
              );
              const ddd = await getDocs(docSnaps);
              let last_three_score = 0;
              ddd.forEach((a, index) => {
                last_three_score = last_three_score + a.data().CalibrationAvg;
              });
              last_three_score = last_three_score / 3;
              if (last_three_score > CalibScore) {
                sign = "down";
              } else if (last_three_score < CalibScore) {
                sign = "up";
              }
            }
          }
          newArray[index] = { ...obj, CalibScore, sign };
          newUsers[index] = { ...obj, CalibScore, sign };
        }
        if (change.type === "removed") {
          let index = newUsers.findIndex(user => change.doc.id === user.uid);
          newUsers.splice(index, 1);
        }
      } catch (error) {
        //do nothing
      }
    });
    await Promise.all(promises);
    setUsers(newUsers);
    setLoading(false);
  };

  const getRemoteConfigs = () => {
    const data = getAll(firebaseRemoteConfig);
    setRemoteConfigData(data);
  };

  const detectChanges = userData => {
    let ChangesMade = false;
    if (remoteConfigData) {
      let DefaultCalibration = JSON.parse(
        remoteConfigData.DefaultCalibration._value
      );
      if (
        userData.ReadCalibrationFromBackend &&
        !userData.ReadCalibrationFromBackend
      ) {
        ChangesMade = true;
      }
      if (
        userData.DefaultCalibrationMoment &&
        userData.DefaultCalibrationMoment !==
          Number(remoteConfigData.DefaultCalibrationMoment._value)
      ) {
        ChangesMade = true;
      }
      if (
        userData.MovementSpeed &&
        userData.MovementSpeed !==
          Number(remoteConfigData.DefaultUserSpeed._value)
      ) {
        ChangesMade = true;
      }
      if (
        userData.PersonalOffset &&
        userData.PersonalOffset !==
          Number(remoteConfigData.DefaultPersonalOffset._value)
      ) {
        ChangesMade = true;
      }
      if (
        userData.RecommendedSessionsPerWeek &&
        userData.RecommendedSessionsPerWeek !==
          Number(remoteConfigData.RecommendedSessionsPerWeek._value)
      ) {
        ChangesMade = true;
      }
      if (userData.CalibrationBackend) {
        if (
          userData.CalibrationBackend.offSetValue &&
          userData.CalibrationBackend.offSetValue !==
            Number(DefaultCalibration.offSetValue)
        ) {
          ChangesMade = true;
        }
        if (
          userData.CalibrationBackend.pendlumLength &&
          userData.CalibrationBackend.pendlumLength !==
            Number(DefaultCalibration.pendlumLength)
        ) {
          ChangesMade = true;
        }
        if (userData.CalibrationBackend.deviceDimensions) {
          if (
            userData.CalibrationBackend.deviceDimensions.width &&
            userData.CalibrationBackend.deviceDimensions.width !==
              Number(DefaultCalibration.deviceDimensions.width)
          ) {
            ChangesMade = true;
          }
          if (
            userData.CalibrationBackend.deviceDimensions.height &&
            userData.CalibrationBackend.deviceDimensions.height !==
              Number(DefaultCalibration.deviceDimensions.height)
          ) {
            ChangesMade = true;
          }
          if (
            userData.CalibrationBackend.deviceDimensions.pixelRatio &&
            userData.CalibrationBackend.deviceDimensions.pixelRatio !==
              Number(DefaultCalibration.deviceDimensions.pixelRatio)
          ) {
            ChangesMade = true;
          }
        }
      }
      return ChangesMade;
    } else {
      return ChangesMade;
    }
  };

  // const checkDateisAfterToday = date => {
  //   return moment().isAfter(date);
  // };

  const values = {
    remoteConfigData,
    Users,
    loading,
    error
  };

  return (
    <UsersDataContext.Provider value={values}>
      {children}
    </UsersDataContext.Provider>
  );
};
